import React, { useContext, useMemo } from "react";
import { Button } from "@foris/avocado-suite";
import { ContextApp, IContextApp } from "@config/Context/contextApp";
import { IParams } from "@models/IParams";
import * as enums from "@utils/enums";
import { IEvent } from "@modules/sections/ISections";
import { useHistory, useParams } from "react-router-dom";
import icons from "@utils/icons";
import ItemInfoRow from "@modules/sections/GridSections/item-info-row";
import styles from "./bookingModalDetails.module.scss";
import { useTranslation } from "react-i18next";
import { Classroom } from "@models/ISchema";

interface BookingModalDetailsProps {
  event: IEvent;
}

const BookingModalDetails = ({ event }: BookingModalDetailsProps) => {
  const { origin, scenario, workspace }: IParams = useParams();
  const history = useHistory();
  const context: IContextApp = useContext(ContextApp);
  const contextUrl = `${workspace}/${scenario}/${origin}`;
  const accessToBookingAdmin = context.user?.permissions?.create;
  const accessToBookingUser =
    context?.modules?.find(item => item.code === "bookings").accesible || false;

  const { t } = useTranslation();

  let day = "";
  let startDate = "";
  let endDate = "";
  let description = "";
  let responsible = "";
  let status = "";
  let bookingId = null;

  if (event && event.resource) {
    const resource = event.resource;
    if (resource.blockRange) {
      if (resource.blockRange.start) {
        day =
          (resource.blockRange.start.day && enums.DAY_NAMES_ES[resource.blockRange.start.day]) ||
          "";
        startDate = resource.blockRange.start.startingTime.replace(":00", "") || "";
      }
      endDate = resource.blockRange.end
        ? resource.blockRange.end.endingTime.replace(":00", "")
        : "";
    }
  }

  if (event && event.bookings) {
    const bookingsInfo = event?.bookings;
    description = bookingsInfo.description;
    bookingId = bookingsInfo.id;
    responsible = bookingsInfo?.responsible?.name
      ? `${bookingsInfo?.responsible?.name ?? "-"} ${bookingsInfo?.responsible?.lastName ?? ""}`
      : t("calendar-view.event-modal.columns.empty-responsible");
    status = bookingsInfo.status;
  }

  const bookingSession = useMemo(() => {
    const bookings = event?.bookings;
    const sessionId = event?.sessionId;

    if (!bookings || sessionId == null) {
      return null;
    }

    return (
      bookings?.sessions?.find(
        session => session?.id != null && String(session.id) === String(sessionId),
      ) ?? null
    );
  }, [event?.bookings, event?.sessionId]);

  const classroomRows = useMemo(() => {
    const classrooms = bookingSession?.classrooms?.length ? bookingSession.classrooms : [null];

    return classrooms.map((classroom: Classroom | null, index) => ({
      key: classroom?.id ?? `classroom-empty-${index}`,
      columns: [
        {
          icon: "building" as const,
          title: `${t("calendar-view.event-modal.columns.classroom")}:`,
          text: classroom?.code ?? t("calendar-view.event-modal.columns.empty-classroom"),
          link: classroom?.id ? `/scheduler/editor/classroom/${contextUrl}/${classroom.id}` : "",
        },
        {
          title: `${t("calendar-view.event-modal.columns.building")}:`,
          text: classroom?.building?.code || "-",
        },
        {
          title: `${t("calendar-view.event-modal.columns.campus")}:`,
          text: classroom?.building?.campus?.code || "-",
        },
      ],
    }));
  }, [bookingSession?.classrooms, contextUrl, t]);

  return (
    <div className={styles.bookingModalDetails}>
      <section className={styles.bookingModalDetails_content}>
        <ItemInfoRow
          columns={[
            {
              icon: "calendar",
              title: `${t("calendar-view.event-modal.columns.schedule")}:`,
              text: `${day ? `${day},` : ""} ${startDate} - ${endDate}`,
            },
          ]}
        />

        {classroomRows.map(({ key, columns }) => (
          <ItemInfoRow key={key} columns={columns} />
        ))}

        <ItemInfoRow
          columns={[
            {
              icon: "circle-info",
              title: `${t("calendar-view.event-modal.columns.status")}:`,
              text: icons.statusIconText(status),
            },
          ]}
        />

        <ItemInfoRow
          columns={[
            {
              icon: "user",
              title: `${t("calendar-view.event-modal.columns.responsible")}:`,
              text: responsible,
            },
          ]}
        />

        <ItemInfoRow
          columns={[
            {
              title: `${t("calendar-view.event-modal.columns.description")}:`,
              text: description,
            },
          ]}
        />
      </section>

      {accessToBookingAdmin && accessToBookingUser && (
        <footer className={styles.bookingModalDetails_footer}>
          <Button
            className={styles.footerAction}
            onClick={() => history.push(`/booking/detail/${contextUrl}/${bookingId}`)}
          >
            {t("calendar-view.event-modal.booking-action-btn")}
          </Button>
        </footer>
      )}
    </div>
  );
};

export default BookingModalDetails;
